import { useMemo, useState } from "react";
import { Table, App, Input, Tooltip, Popover, Button } from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
  CopyOutlined,
  DownloadOutlined,
  SyncOutlined,
  PrinterOutlined,
  FileProtectOutlined,
  CreditCardOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { Device } from "../../../../api/devices";
import { triggerFiscalApiUpdate } from "../../../../api/devices";
import type { UserListItemOut } from "../../../../api/auth";
import {
  useUpdateDevice,
  useDeleteDevice,
  useFiscalApiLatest,
} from "../../../../hooks/useDevices";
import { usePaymentMethods } from "../../../../hooks/usePaymentMethods";
import { useStations } from "../../../../hooks/useStations";
import { usePrinterNotifications } from "../../../../hooks/usePrinterNotifications";
import { getTranslation } from "../../../../utils/translations";
import DeviceFormModal, { type DeviceFormValues } from "./DeviceFormModal";
import DevicePaymentMethodsViewModal from "./DevicePaymentMethodsViewModal";
import DeleteConfirmModal from "../DeleteConfirmModal";
import { useTranslation } from "react-i18next";
import {
  ConfigPanel,
  PanelAddButton,
  PanelIconAction,
} from "../panel/ConfigPanel";
import { PANEL_ACCENTS } from "../panel/panelAccents";

const R = {
  bg: "var(--color-canvas)",
  surface: "var(--color-surface)",
  border: "var(--color-shade-2)",
  borderMed: "var(--color-shade-3)",
  textSoft: "var(--color-body)",
  textMuted: "var(--color-muted)",
  ink: "var(--color-ink)",
  // „Има ъпдейт" на FiscalAPI остава оранжево и в синята секция —
  // това е семантичен цвят за внимание, не секционен акцент.
  // brand/danger са рамки в плътния акцентен цвят — еднакви в двете теми.
  brand: "#D97706",
  brandDeep: "var(--color-brand-deep-ink)",
  brandTint: "var(--color-brand-tint)",
  danger: "#DC2626",
  dangerDeep: "var(--color-danger-ink)",
  mono: '"JetBrains Mono", ui-monospace, monospace',
};

// Едно FiscalAPI се обявява наново на всеки connect и на всеки 5 минути.
// Три пропуснати heartbeat-а значи, че или машината е спряна, или е загубила
// брокера — записът стои (никой не го е оттеглил), но вече не е доказателство
// за жива връзка.
const PRESENCE_STALE_MS = 15 * 60 * 1000;

function isStale(announcedAt: string | null | undefined): boolean {
  if (!announcedAt) return true;
  const ts = Date.parse(announcedAt);
  if (Number.isNaN(ts)) return true;
  return Date.now() - ts > PRESENCE_STALE_MS;
}

// Числово сравнение на "1.46.0"-стил версии. Непозната/липсваща версия НЕ
// е "по-стара" — старите FiscalAPI не се обявяват изобщо и бутон „Обнови"
// върху непознато би обещавал команда, която никой не слуша.
function isVersionOlder(
  current: string | null | undefined,
  latest: string | null | undefined
): boolean {
  if (!current || !latest) return false;
  const a = current.split(".").map((n) => parseInt(n, 10) || 0);
  const b = latest.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] ?? 0;
    const y = b[i] ?? 0;
    if (x !== y) return x < y;
  }
  return false;
}

interface Props {
  // Устройствата на ТАЗИ секция (= всички от нейния тип; типът е фиксиран
  // и в модала, така че това стига и за „заетите" акаунти).
  devices: Device[];
  users: UserListItemOut[];
  // Коя секция е това: ПОС принтери (type="pos"), фискални принтери
  // (type="fiscal") или терминали за плащане (type="paymentTerminal").
  // Определя акцента, колоните и текстовете.
  variant: "pos" | "fiscal" | "terminal";
  onAddDevice: () => void;
  isNarrow?: boolean;
  orgUrl?: string | null;
}

const DevicesTable = ({
  devices,
  users,
  variant,
  onAddDevice,
  isNarrow = false,
  orgUrl = null,
}: Props) => {
  const { t, i18n } = useTranslation();
  const updateDeviceMutation = useUpdateDevice();
  const deleteDeviceMutation = useDeleteDevice();
  const { data: allPaymentMethods = [] } = usePaymentMethods();
  const { data: stations = [] } = useStations();
  const { message } = App.useApp();
  const { notifyPrinterError } = usePrinterNotifications();
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [deviceToDelete, setDeviceToDelete] = useState<Device | null>(null);
  const [pmViewDevice, setPmViewDevice] = useState<Device | null>(null);
  const [searchText, setSearchText] = useState("");
  const { data: fiscalApiLatest } = useFiscalApiLatest();
  const [updatingDeviceId, setUpdatingDeviceId] = useState<number | null>(null);

  // Отдалечен ъпдейт на FiscalAPI инсталацията зад устройството. Чакаме само
  // ack-а („тръгна"/отказ с причина) — самият ъпдейт тече минути, завършва с
  // рестарт на гейтуея и новата версия се появява при следващия presence.
  const handleFiscalApiUpdate = async (device: Device) => {
    setUpdatingDeviceId(device.id);
    try {
      const res = await triggerFiscalApiUpdate(device.id);
      if (res.status === "success") {
        message.success(t("devices.fiscalapiUpdateStarted"));
      } else {
        notifyPrinterError(res.error || t("devices.fiscalapiUpdateFailed"));
      }
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      notifyPrinterError(detail || t("devices.fiscalapiUpdateFailed"));
    } finally {
      setUpdatingDeviceId(null);
    }
  };

  // Типовият филтър отпадна — самото разделение на секции ПОС/Фискални
  // играе ролята му; остава само текстовото търсене.
  const filteredDevices = useMemo(() => {
    if (!searchText.trim()) return devices;
    const q = searchText.toLowerCase();
    return devices.filter((d) => {
      const nameMatch = d.name.toLowerCase().includes(q);
      const guidMatch = d.guid.toLowerCase().includes(q);
      const userMatch = d.userIds.some((uid) => {
        const u = users.find((x) => x.id === uid);
        return u?.username.toLowerCase().includes(q);
      });
      return nameMatch || guidMatch || userMatch;
    });
  }, [devices, searchText, users]);

  const handleEdit = (device: Device) => {
    setSelectedDevice(device);
    setEditModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deviceToDelete) return;
    try {
      await deleteDeviceMutation.mutateAsync(deviceToDelete.id);
      message.success(t("devices.deleteSuccess"));
      setDeviceToDelete(null);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      message.error(detail || t("devices.deleteError"));
      throw error;
    }
  };

  const handleEditSubmit = async (deviceId: number, values: DeviceFormValues) => {
    try {
      await updateDeviceMutation.mutateAsync({ id: deviceId, data: values });
      message.success(t("devices.editSuccess"));
      setEditModalVisible(false);
      setSelectedDevice(null);
    } catch (error: unknown) {
      const detail = (error as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail;
      message.error(detail || t("devices.editError"));
      throw error;
    }
  };

  const isFiscalSection = variant === "fiscal";
  // Всяка секция е един тип устройство → колоната „Тип" е излишна; разликата
  // е само в акцента (оранжево/синьо/зелено), иконата и текстовете.
  const sectionMeta = {
    pos: {
      accent: PANEL_ACCENTS.amber,
      scope: "dev-list-pos",
      icon: <PrinterOutlined />,
      kicker: t("devices.posSectionKicker"),
      title: t("devices.posDevices"),
      addLabel: t("devices.addPosDevice"),
      emptyText: t("devices.noPosDevices"),
    },
    fiscal: {
      accent: PANEL_ACCENTS.blue,
      scope: "dev-list-fiscal",
      icon: <FileProtectOutlined />,
      kicker: t("devices.fiscalSectionKicker"),
      title: t("devices.fiscalDevices"),
      addLabel: t("devices.addFiscalDevice"),
      emptyText: t("devices.noFiscalDevices"),
    },
    terminal: {
      accent: PANEL_ACCENTS.green,
      scope: "dev-list-terminal",
      icon: <CreditCardOutlined />,
      kicker: t("devices.terminalSectionKicker"),
      title: t("devices.terminalDevices"),
      addLabel: t("devices.addTerminalDevice"),
      emptyText: t("devices.noTerminalDevices"),
    },
  }[variant];
  const accent = sectionMeta.accent;
  const panelScope = sectionMeta.scope;

  const columns: ColumnsType<Device> = [
    {
      title: t("devices.name"),
      dataIndex: "name",
      key: "name",
      width: 200,
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => (
        <span
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: R.ink,
            letterSpacing: -0.1,
          }}
        >
          {name}
        </span>
      ),
    },
    {
      title: t("devices.uniqueNumber"),
      dataIndex: "guid",
      key: "guid",
      width: 360,
      render: (guid: string) => (
        <div className="flex items-center" style={{ gap: 6 }}>
          <span
            style={{
              fontFamily: R.mono,
              fontSize: 11.5,
              color: R.textSoft,
              letterSpacing: 0.1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              maxWidth: "100%",
            }}
          >
            {guid}
          </span>
          <Tooltip title={guid}>
            <button
              type="button"
              onClick={() => {
                navigator.clipboard.writeText(guid);
                message.success(t("devices.copySuccess"));
              }}
              style={{
                height: 24,
                width: 24,
                borderRadius: 6,
                border: "none",
                background: "transparent",
                color: R.textMuted,
                cursor: "pointer",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background 120ms, color 120ms",
                flexShrink: 0,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = accent.tint;
                e.currentTarget.style.color = accent.deep;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = R.textMuted;
              }}
            >
              <CopyOutlined style={{ fontSize: 12 }} />
            </button>
          </Tooltip>
        </div>
      ),
    },
    {
      title: t("devices.users"),
      dataIndex: "userIds",
      key: "userIds",
      width: 180,
      render: (userIds: number[]) => {
        if (!userIds || userIds.length === 0) {
          return (
            <span style={{ color: R.textMuted, fontSize: 12.5 }}>—</span>
          );
        }
        const resolved = userIds.map((uid) => {
          const user = users.find((u) => u.id === uid);
          return {
            id: uid,
            name: user?.username || `ID ${uid}`,
          };
        });
        const tooltipContent = (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {resolved.map((u) => (
              <div
                key={u.id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: R.textMuted,
                    flexShrink: 0,
                  }}
                />
                {u.name}
              </div>
            ))}
          </div>
        );
        return (
          <Tooltip title={tooltipContent} placement="topLeft">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 10px",
                background: R.bg,
                color: R.textSoft,
                border: `1px solid ${R.border}`,
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                cursor: "default",
              }}
            >
              {t("devices.usersCount", { count: userIds.length })}
            </span>
          </Tooltip>
        );
      },
    },
    // Станциите важат само за POS принтери → колоната е само в ПОС секцията.
    ...(variant === "pos"
      ? ([
    {
      title: t("devices.stations"),
      key: "stationIds",
      width: 180,
      render: (_: unknown, record: Device) => {
        if (record.type !== "pos") {
          return (
            <span style={{ color: R.textMuted, fontSize: 12.5 }}>—</span>
          );
        }
        const list = record.stationIds ?? [];
        if (list.length === 0) {
          return (
            <span style={{ color: R.textMuted, fontSize: 12.5 }}>—</span>
          );
        }
        const resolved = list.map((sid) => {
          const station = stations.find((s) => s.id === sid);
          return {
            id: sid,
            name: station
              ? getTranslation(
                  station.translations,
                  i18n.language,
                  station.slug,
                )
              : `ID ${sid}`,
          };
        });
        const tooltipContent = (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {resolved.map((s) => (
              <div
                key={s.id}
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                }}
              >
                <span
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: R.textMuted,
                    flexShrink: 0,
                  }}
                />
                {s.name}
              </div>
            ))}
          </div>
        );
        return (
          <Tooltip title={tooltipContent} placement="topLeft">
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                padding: "3px 10px",
                background: R.bg,
                color: R.textSoft,
                border: `1px solid ${R.border}`,
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                cursor: "default",
              }}
            >
              {t("devices.stationsCount", { count: list.length })}
            </span>
          </Tooltip>
        );
      },
    },
        ] as ColumnsType<Device>)
      : []),
    // Методите на плащане важат само за фискален принтер → колоната е само
    // във фискалната секция.
    ...(isFiscalSection
      ? ([
    {
      title: t("devices.paymentMethods"),
      key: "paymentMethods",
      render: (_: unknown, record: Device) => {
        // Методите на плащане важат само за фискален принтер.
        if (record.type !== "fiscal") {
          return (
            <span style={{ color: R.textMuted, fontSize: 12.5 }}>
              {t("devices.noPaymentMethodsForDevice")}
            </span>
          );
        }
        const list = record.paymentMethods ?? [];
        if (list.length === 0) {
          return (
            <span style={{ color: R.textMuted, fontSize: 12.5 }}>
              {t("devices.noPaymentMethodsForDevice")}
            </span>
          );
        }
        return (
          <button
            type="button"
            onClick={() => setPmViewDevice(record)}
            style={{
              padding: 0,
              border: "none",
              background: "transparent",
              color: accent.deep,
              fontSize: 12.5,
              fontWeight: 600,
              letterSpacing: -0.1,
              cursor: "pointer",
              textDecoration: "underline",
              textDecorationColor: accent.strongBorder,
              textUnderlineOffset: 3,
              transition: "color 120ms, text-decoration-color 120ms",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = accent.main;
              e.currentTarget.style.textDecorationColor = accent.main;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = accent.deep;
              e.currentTarget.style.textDecorationColor = accent.strongBorder;
            }}
          >
            {t("devicePaymentMethods.viewButton")}
          </button>
        );
      },
    },
        ] as ColumnsType<Device>)
      : []),
    {
      title: t("devices.connectedClients"),
      key: "connectedClients",
      width: 200,
      render: (_: unknown, record: Device) => {
        const list = record.connectedClients ?? [];
        if (list.length === 0) {
          // Празно ≠ "няма закачено устройство". Старите FiscalAPI версии не
          // се обявяват изобщо, така че тире + пояснение е честният отговор;
          // червено "0 връзки" би било лъжа за половината инсталации.
          return (
            <Tooltip title={t("devices.connectedClientsUnknown")}>
              <span style={{ color: R.textMuted, fontSize: 12.5 }}>—</span>
            </Tooltip>
          );
        }
        const conflict = list.length > 1;
        const latestVer = fiscalApiLatest?.version || null;
        // Обновима е само ЖИВА инсталация с обявена по-стара версия —
        // командата пътува по MQTT и никой не я чака на спряна машина.
        const updatable =
          !!latestVer &&
          list.some(
            (c) => !isStale(c.announced_at) && isVersionOlder(c.app_version, latestVer)
          );
        const popoverContent = (
          <div style={{ display: "flex", flexDirection: "column", gap: 8, maxWidth: 300 }}>
            {conflict && (
              <div style={{ fontSize: 12, fontWeight: 700, color: R.dangerDeep }}>
                {t("devices.connectedClientsConflict")}
              </div>
            )}
            {list.map((c) => {
              const outdated =
                !!latestVer && isVersionOlder(c.app_version, latestVer);
              return (
                <div
                  key={c.client_id}
                  style={{ display: "flex", flexDirection: "column", fontSize: 12 }}
                >
                  <span style={{ fontWeight: 600 }}>
                    {c.client_name || c.client_id}
                    {isStale(c.announced_at)
                      ? ` · ${t("devices.connectedClientsStale")}`
                      : ""}
                  </span>
                  <span style={{ opacity: 0.75 }}>
                    {[c.hostname, c.device_name, c.device_model]
                      .filter(Boolean)
                      .join(" · ")}
                    {c.app_version && (
                      <>
                        {(c.hostname || c.device_name || c.device_model) && " · "}
                        <span
                          style={{
                            color: outdated ? R.brandDeep : undefined,
                            fontWeight: outdated ? 700 : undefined,
                          }}
                        >
                          v{c.app_version}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
            {updatable && (
              <Button
                size="small"
                type="primary"
                icon={<SyncOutlined />}
                loading={updatingDeviceId === record.id}
                onClick={() => handleFiscalApiUpdate(record)}
                style={{ alignSelf: "flex-start" }}
              >
                {t("devices.fiscalapiUpdateButton", { version: latestVer })}
              </Button>
            )}
          </div>
        );
        return (
          <Popover
            content={popoverContent}
            placement="topLeft"
            trigger={updatable ? ["hover", "click"] : ["hover"]}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "3px 10px",
                background: conflict ? "var(--color-danger-tint)" : updatable ? R.brandTint : R.bg,
                color: conflict ? R.dangerDeep : updatable ? R.brandDeep : R.textSoft,
                border: `1px solid ${
                  conflict ? R.danger : updatable ? R.brand : R.border
                }`,
                borderRadius: 999,
                fontSize: 11.5,
                fontWeight: 700,
                letterSpacing: 0.2,
                cursor: updatable ? "pointer" : "default",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {conflict
                ? `⚠ ${t("devices.connectedClientsCount", { count: list.length })}`
                : list[0].client_name || list[0].client_id}
              {updatable && !conflict && <SyncOutlined style={{ fontSize: 11 }} />}
            </span>
          </Popover>
        );
      },
    },
    {
      title: t("devices.actions"),
      key: "actions",
      width: 120,
      align: "right",
      render: (_: unknown, record: Device) => (
        <div style={{ display: "inline-flex", gap: 6 }}>
          <PanelIconAction
            onClick={() => handleEdit(record)}
            label={t("common.edit", "Edit")}
            accent={accent}
          >
            <EditOutlined />
          </PanelIconAction>
          <PanelIconAction
            onClick={() => setDeviceToDelete(record)}
            label={t("common.delete", "Delete")}
            danger
            accent={accent}
          >
            <DeleteOutlined />
          </PanelIconAction>
        </div>
      ),
    },
  ];

  const totalCount = filteredDevices.length;

  // Общ стил на чиповете-инструменти (org адрес, FiscalAPI инсталатор).
  const toolChipStyle: React.CSSProperties = {
    border: `1px solid ${R.borderMed}`,
    background: R.surface,
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 12,
    color: R.textSoft,
    cursor: "pointer",
    minWidth: 0,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    transition: "color 120ms, border-color 120ms, background 120ms",
  };
  const toolChipHoverOn = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.color = accent.deep;
    e.currentTarget.style.borderColor = accent.main;
    e.currentTarget.style.background = accent.tint;
  };
  const toolChipHoverOff = (e: React.MouseEvent<HTMLElement>) => {
    e.currentTarget.style.color = R.textSoft;
    e.currentTarget.style.borderColor = R.borderMed;
    e.currentTarget.style.background = R.surface;
  };

  return (
    <>
      <ConfigPanel
        scope={panelScope}
        accent={accent}
        icon={sectionMeta.icon}
        kicker={sectionMeta.kicker}
        title={sectionMeta.title}
        count={totalCount}
        isNarrow={isNarrow}
        isEmpty={filteredDevices.length === 0}
        emptyText={sectionMeta.emptyText}
        toolbar={
          <>
            {/* Адресът на организацията — чип с копиране; до него линкът за
                сваляне на последния FiscalAPI инсталатор (от манифеста на
                update сървъра, през BE проксито). Стоят при фискалните —
                там се настройва FiscalAPI инсталацията. */}
            {isFiscalSection && orgUrl && (
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(orgUrl);
                  message.success(t("devices.orgUrlCopied"));
                }}
                title={orgUrl}
                style={{ ...toolChipStyle, fontFamily: R.mono }}
                onMouseEnter={toolChipHoverOn}
                onMouseLeave={toolChipHoverOff}
              >
                <span
                  style={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {orgUrl.replace(/^https?:\/\//, "")}
                </span>
                <CopyOutlined style={{ fontSize: 13, flexShrink: 0 }} />
              </button>
            )}
            {isFiscalSection && fiscalApiLatest?.url && (
              <a
                href={fiscalApiLatest.url}
                title={t("devices.fiscalapiDownload")}
                style={{
                  ...toolChipStyle,
                  fontWeight: 600,
                  whiteSpace: "nowrap",
                  textDecoration: "none",
                }}
                onMouseEnter={toolChipHoverOn}
                onMouseLeave={toolChipHoverOff}
              >
                <DownloadOutlined style={{ fontSize: 13, flexShrink: 0 }} />
                <span>
                  FiscalAPI{" "}
                  {fiscalApiLatest.version ? `v${fiscalApiLatest.version}` : ""}
                </span>
              </a>
            )}
            <Input
              placeholder={t("devices.searchPlaceholder")}
              prefix={<SearchOutlined style={{ color: R.textMuted }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{
                width: isNarrow ? "100%" : 240,
              }}
            />
            <PanelAddButton
              onClick={onAddDevice}
              label={sectionMeta.addLabel}
              accent={accent}
              isNarrow={isNarrow}
              dataAttr="data-devices-add-button"
            />
          </>
        }
      >
        <Table
          columns={columns}
          dataSource={filteredDevices}
          rowKey="id"
          pagination={false}
          scroll={{ x: isFiscalSection ? 900 : 820 }}
          size="small"
        />
      </ConfigPanel>

      {selectedDevice && (
        <DeviceFormModal
          open={editModalVisible}
          device={selectedDevice}
          initialType={selectedDevice.type}
          users={users}
          devices={devices}
          onSubmit={(values) => handleEditSubmit(selectedDevice.id, values)}
          onCancel={() => {
            setEditModalVisible(false);
            setSelectedDevice(null);
          }}
        />
      )}

      <DeleteConfirmModal
        open={!!deviceToDelete}
        title={t("devices.deleteConfirmTitle")}
        description={t("devices.deleteConfirmContent", {
          name: deviceToDelete?.name ?? "",
        })}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeviceToDelete(null)}
      />

      <DevicePaymentMethodsViewModal
        open={!!pmViewDevice}
        device={pmViewDevice}
        paymentMethods={allPaymentMethods}
        onClose={() => setPmViewDevice(null)}
      />
    </>
  );
};

export default DevicesTable;
